import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

const BASKET_STORAGE_KEY = 'divine-veg-basket-v2';

export const BasketContext = createContext();

function readStoredBasket() {
    try {
        const stored = JSON.parse(localStorage.getItem(BASKET_STORAGE_KEY));
        return Array.isArray(stored)
            ? stored.filter(line => line && typeof line.lineId === 'string' && Number.isInteger(line.menuItemId))
            : [];
    } catch (error) {
        return [];
    }
}

export const BasketProvider = ({ children }) => {
    const [basket, setBasket] = useState(readStoredBasket);

    useEffect(() => {
        localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(basket));
    }, [basket]);

    const addToBasket = (item, variant = null) => {
        const menuItemId = Number(item.id);
        const variantId = variant?.id ? Number(variant.id) : null;
        const lineId = `${menuItemId}:${variantId || 'standard'}`;
        const unitPrice = Number(variant?.price ?? item.price);

        setBasket(currentBasket => {
            const existingLine = currentBasket.find(line => line.lineId === lineId);

            if (existingLine) {
                return currentBasket.map(line => (
                    line.lineId === lineId ? { ...line, quantity: line.quantity + 1 } : line
                ));
            }

            return [...currentBasket, {
                lineId,
                menuItemId,
                variantId,
                name: item.name,
                description: item.description || '',
                image: item.image || '',
                category: item.category || '',
                variantName: variant?.name || null,
                optionLabel: variant?.size || null,
                unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
                quantity: 1,
            }];
        });
    };

    const removeFromBasket = lineId => {
        setBasket(currentBasket => currentBasket.filter(line => line.lineId !== lineId));
    };

    const updateItemQuantity = (lineId, delta) => {
        setBasket(currentBasket => currentBasket.flatMap(line => {
            if (line.lineId !== lineId) return [line];
            const quantity = line.quantity + delta;
            return quantity > 0 ? [{ ...line, quantity }] : [];
        }));
    };

    const clearBasket = () => setBasket([]);
    const removeSoldOutItems = useCallback((menuItems) => {
        const soldOutIds = new Set((menuItems || [])
            .filter(item => item?.isSoldOut)
            .map(item => Number(item.id)));
        const removedItems = basket.filter(line => soldOutIds.has(line.menuItemId));

        if (removedItems.length) {
            setBasket(currentBasket => currentBasket.filter(line => !soldOutIds.has(line.menuItemId)));
        }

        return removedItems;
    }, [basket]);
    const total = useMemo(
        () => basket.reduce((sum, line) => sum + (line.unitPrice * line.quantity), 0),
        [basket]
    );
    const itemCount = useMemo(
        () => basket.reduce((sum, line) => sum + line.quantity, 0),
        [basket]
    );

    return (
        <BasketContext.Provider value={{ basket, addToBasket, removeFromBasket, updateItemQuantity, total, itemCount, clearBasket, removeSoldOutItems }}>
            {children}
        </BasketContext.Provider>
    );
};
